import { Component, OnInit } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { RouterModule, Router } from '@angular/router';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { FormsModule } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { SelectModule } from 'primeng/select';
import { TagModule } from 'primeng/tag';
import { ProgressBarModule } from 'primeng/progressbar';
import { PosmProject, PosmProjectSummary } from '../models/posm-project.model';
import { PosmService } from '../posm.service';
import { SessionStorageService } from '../../services/session-storage.service';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageMetricStripComponent } from '../../shared/page-layout/page-metric-strip.component';
import { PageSearchPanelComponent } from '../../shared/page-layout/page-search-panel.component';
import { PageDataPanelComponent } from '../../shared/page-layout/page-data-panel.component';
import { PageMetric } from '../../shared/page-layout/page-layout.models';
import { PosmSetupData } from '../models/posm-planning.model';

type SelectOption = { label: string; value: string | null };

@Component({
    selector: 'app-posm-project-list',
    standalone: true,
    imports: [
        CommonModule,
        RouterModule,
        TableModule,
        ButtonModule,
        DatePipe,
        FormsModule,
        InputTextModule,
        SelectModule,
        TagModule,
        ProgressBarModule,
        IftaLabelModule,
        InputGroupModule,
        InputGroupAddonModule,
        PageShellComponent,
        PageHeroComponent,
        PageMetricStripComponent,
        PageSearchPanelComponent,
        PageDataPanelComponent
    ],
    templateUrl: './posm-project-list.component.html',
    styleUrls: ['./posm-project-list.component.css']
})
export class PosmProjectListComponent implements OnInit {

    projects: PosmProject[] = [];
    loading = true;
    summaryLoading = false;
    owningCompanyId!: number;
    searchText = '';
    statusFilter: string | null = null;
    priorityFilter: string | null = null;
    jobTypeFilter: string | null = null;
    clientFilter: string | null = null;
    brandFilter: string | null = null;
    page = 0;
    pageSize = 10;
    totalProjects = 0;
    private loadSequence = 0;

    private statusSeedOptions: SelectOption[] = [];
    private projectSummaries: Record<number, PosmProjectSummary> = {};
    private summaryLoadSequence = 0;

    constructor(
        private posmService: PosmService,
        private sessionStorageService: SessionStorageService,
        private router: Router
    ) { }

    ngOnInit(): void {
        const customer = this.sessionStorageService.getCustomer();
        if (customer?.company?.id) {
            this.owningCompanyId = customer.company.id;
            this.loadStatusOptions();
            this.loadProjects();
            return;
        }
        console.error('No owning company ID found for the current user session.');
        this.loading = false;
    }

    loadProjects(): void {
        const sequence = ++this.loadSequence;
        this.loading = true;
        this.posmService.getProjectsPage(this.owningCompanyId, this.page, this.pageSize, {
            search: this.searchText, status: this.statusFilter, priority: this.priorityFilter,
            jobType: this.jobTypeFilter, client: this.clientFilter, brand: this.brandFilter
        }).subscribe({
            next: (data) => {
                if (sequence !== this.loadSequence) return;
                this.projects = data.content || [];
                this.totalProjects = data.totalElements || 0;
                this.loading = false;
                this.loadProjectSummaries(this.projects);
            },
            error: (err) => {
                if (sequence !== this.loadSequence) return;
                console.error('Error fetching POSM executions', err);
                this.loading = false;
                this.projects = [];
                this.totalProjects = 0;
                this.projectSummaries = {};
                this.summaryLoading = false;
                this.summaryLoadSequence++;
            }
        });
    }

    onPage(event: { first?: number; rows?: number }): void {
        this.pageSize = event.rows || 10;
        this.page = Math.floor((event.first || 0) / this.pageSize);
        this.loadProjects();
    }

    private loadProjectSummaries(projects: PosmProject[]): void {
        const sequence = ++this.summaryLoadSequence;
        const projectIds = projects
            .map(project => project.id)
            .filter((id): id is number => typeof id === 'number');
        if (!projectIds.length) {
            this.projectSummaries = {};
            this.summaryLoading = false;
            return;
        }

        this.summaryLoading = true;
        this.posmService.getProjectSummaries(projectIds, this.owningCompanyId).subscribe({
            next: (rows) => {
                if (sequence !== this.summaryLoadSequence) return;
                this.projectSummaries = (rows || []).reduce<Record<number, PosmProjectSummary>>((acc, row) => {
                    if (row.projectId != null) {
                        acc[row.projectId] = row;
                    }
                    return acc;
                }, {});
                this.summaryLoading = false;
            },
            error: (err) => {
                if (sequence !== this.summaryLoadSequence) return;
                console.error('Error fetching POSM execution summaries', err);
                this.projectSummaries = {};
                this.summaryLoading = false;
            }
        });
    }

    private dateValue(value?: Date | string): number {
        if (!value) {
            return 0;
        }
        const parsed = new Date(value).getTime();
        return Number.isNaN(parsed) ? 0 : parsed;
    }

    viewProject(project: PosmProject): void {
        this.router.navigate(['/posm/executions', project.id]);
    }

    createProject(): void {
        this.router.navigate(['/posm/executions/create']);
    }

    openLogisticsProject(project: PosmProject): void {
        if (project.logisticsProjectId) {
            this.router.navigate(['/logistics-projects', project.logisticsProjectId]);
        }
    }

    loadStatusOptions(): void {
        this.posmService.getSetup('PROJECT_STATUS', this.owningCompanyId).subscribe({
            next: (rows: PosmSetupData[]) => {
                this.statusSeedOptions = (rows || []).reduce<SelectOption[]>((options, row) => {
                        const normalized = this.normalizeCode(row.code || row.name);
                        if (!normalized) {
                            return options;
                        }
                        options.push({
                            label: this.formatLabel(row.name || row.code),
                            value: normalized
                        });
                        return options;
                    }, []);
            },
            error: () => {
                this.statusSeedOptions = [];
            }
        });
    }

    get statusOptions(): SelectOption[] {
        const options = new Map<string, string>();
        this.statusSeedOptions.forEach(option => {
            if (option.value) {
                options.set(option.value, option.label);
            }
        });
        this.projects.forEach(project => {
            const value = this.normalizeCode(project.status);
            if (value) {
                options.set(value, this.projectStatusLabel(project));
            }
        });
        return this.toSortedOptions(options);
    }

    get priorityOptions(): SelectOption[] {
        return [{ label: 'All', value: null }, { label: 'Normal', value: 'NORMAL' },
            { label: 'High', value: 'HIGH' }, { label: 'High Priority', value: 'HIGH_PRIORITY' },
            { label: 'Urgent', value: 'URGENT' }];
    }

    get activeSearchCount(): number {
        return [
            this.searchText,
            this.statusFilter,
            this.priorityFilter,
            this.jobTypeFilter,
            this.clientFilter,
            this.brandFilter
        ].filter(value => !!value).length;
    }

    metrics(): PageMetric[] {
        const active = this.projects.filter(project => !this.isTerminalProject(project)).length;
        const urgent = this.projects.filter(project => {
            const priority = this.normalizeCode(project.jobPriority);
            return priority === 'URGENT' || priority === 'HIGH_PRIORITY' || priority === 'HIGH';
        }).length;
        const overdue = this.projects.filter(project => this.isOverdue(project)).length;
        const summaries = Object.values(this.projectSummaries).filter(summary => this.summaryTaskCount(summary) > 0);
        const averageProgress = summaries.length
            ? Math.round(summaries.reduce((total, summary) => total + this.summaryCompletion(summary), 0) / summaries.length)
            : null;

        return [
            { label: 'Executions', value: this.totalProjects, hint: `${active} active on this page`, tone: 'info' },
            { label: 'Urgent / High', value: urgent, hint: 'on this page', tone: urgent ? 'warn' : 'success' },
            { label: 'Overdue', value: overdue, hint: 'on this page', tone: overdue ? 'danger' : 'success' },
            {
                label: 'Average Progress',
                value: averageProgress != null ? `${averageProgress}%` : 'N/A',
                hint: summaries.length ? `${summaries.length} summaries on this page` : 'delivery schedules not generated yet',
                tone: averageProgress && averageProgress >= 100 ? 'success' : 'info'
            }
        ];
    }

    clearSearch(): void {
        this.searchText = '';
        this.statusFilter = null;
        this.priorityFilter = null;
        this.jobTypeFilter = null;
        this.clientFilter = null;
        this.brandFilter = null;
        this.applySearch();
    }

    applySearch(): void {
        this.page = 0;
        this.loadProjects();
    }

    projectClientName(project: PosmProject): string {
        return project.crmClient?.companyName || project.client?.name || 'Unassigned Client';
    }

    projectBrandName(project: PosmProject): string {
        return project.brandName || 'Unassigned Brand';
    }

    projectClientAccount(project: PosmProject): string {
        return project.clientAccountName || 'Not linked';
    }

    projectContact(project: PosmProject): string {
        return project.clientContactName
            || project.clientContact?.name
            || project.clientContactPhone
            || project.clientContactEmail
            || 'No contact assigned';
    }

    projectMerchandiser(project: PosmProject): string {
        return project.crmMerchandiser?.name
            || project.merchandiser?.name
            || project.merchandiser?.customerName
            || 'Not assigned';
    }

    projectExecutionStrategy(project: PosmProject): string {
        return this.formatLabel(project.executionStrategy) || 'Strategy pending';
    }

    projectJobTypeGroup(project: PosmProject): string {
        const normalized = this.normalizeText(project.jobTypeGroup || this.projectJobTypes(project)[0] || project.jobType);
        if (['installation', 'delivery', 'removal', 'collection'].includes(normalized)) {
            return 'Logistics';
        }
        if (['refurbishment', 'rebranding'].includes(normalized)) {
            return 'Refurbishment and Rebranding';
        }
        if (['site survey', 'site_survey'].includes(normalized)) {
            return 'Site Survey';
        }
        return project.jobTypeGroup || 'Project';
    }

    projectJobTypes(project: PosmProject): string[] {
        const scopes = (project.jobTypes || [])
            .map(scope => this.normalizeJobTypeLabel(scope))
            .filter(Boolean);
        if (scopes.length) {
            return Array.from(new Set(scopes));
        }
        const legacy = this.normalizeJobTypeLabel(project.jobType);
        return legacy ? [legacy] : [];
    }

    projectJobTypeLabel(project: PosmProject): string {
        const scopes = this.projectJobTypes(project);
        return scopes.length ? scopes.join(' + ') : project.jobType || project.jobTypeGroup || 'Unassigned';
    }

    projectJobTypeDisplay(project: PosmProject): string {
        const group = this.projectJobTypeGroup(project);
        const type = this.projectJobTypeLabel(project);
        if (!group) {
            return type;
        }
        if (!type || this.normalizeText(group) === this.normalizeText(type)) {
            return group;
        }
        return `${group} - ${type}`;
    }

    projectPriorityLabel(project: PosmProject): string {
        const normalized = this.normalizeCode(project.jobPriority);
        if (!normalized) {
            return 'Normal';
        }
        if (normalized === 'HIGH_PRIORITY') {
            return 'High Priority';
        }
        return this.formatLabel(normalized);
    }

    projectPrioritySeverity(project: PosmProject): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
        const normalized = this.normalizeCode(project.jobPriority);
        if (normalized === 'URGENT') {
            return 'danger';
        }
        if (normalized === 'HIGH_PRIORITY') {
            return 'warn';
        }
        if (!normalized) {
            return 'secondary';
        }
        return 'info';
    }

    projectStatusLabel(project: PosmProject): string {
        const normalized = this.normalizeCode(project.status);
        if (!normalized || normalized === 'NEW') {
            return 'Draft';
        }
        return this.formatLabel(normalized);
    }

    projectStatusSeverity(project: PosmProject): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
        const normalized = this.normalizeCode(project.status);
        if (['CLOSED', 'COMPLETED', 'DELIVERED'].includes(normalized)) {
            return 'success';
        }
        if (['CANCELLED', 'REJECTED', 'ERROR'].includes(normalized)) {
            return 'danger';
        }
        if (!normalized || ['NEW', 'DRAFT', 'PENDING'].includes(normalized)) {
            return 'warn';
        }
        return 'info';
    }

    projectLifecycleLabel(project: PosmProject): string {
        const closure = this.normalizeCode(project.closureStatus);
        if (closure === 'CLOSED' || !!project.closedAt) {
            return 'Closed';
        }
        if (project.reopenedAt) {
            return 'Reopened';
        }
        if (this.isOverdue(project)) {
            return 'Overdue';
        }
        if (this.isTerminalProject(project)) {
            return 'Completed';
        }
        return 'Active';
    }

    projectLifecycleSeverity(project: PosmProject): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
        const lifecycle = this.projectLifecycleLabel(project);
        switch (lifecycle) {
            case 'Closed':
            case 'Completed':
                return 'success';
            case 'Reopened':
                return 'warn';
            case 'Overdue':
                return 'danger';
            case 'Active':
                return 'info';
            default:
                return 'secondary';
        }
    }

    projectUpdatedBy(project: PosmProject): string {
        return project.updatedByActor?.name
            || project.createdByActor?.name
            || 'System';
    }

    projectSummary(project: PosmProject): PosmProjectSummary | null {
        return project.id != null ? this.projectSummaries[project.id] || null : null;
    }

    hasTaskProgress(project: PosmProject): boolean {
        return this.projectTaskCount(project) > 0;
    }

    projectProgress(project: PosmProject): number {
        const summary = this.projectSummary(project);
        if (!summary) {
            return 0;
        }
        const completion = Number(summary.completionPercentage || 0);
        if (!Number.isFinite(completion)) {
            return 0;
        }
        return Math.max(0, Math.min(100, Math.round(completion)));
    }

    projectTaskCount(project: PosmProject): number {
        return this.summaryTaskCount(this.projectSummary(project));
    }

    projectCompletedTaskCount(project: PosmProject): number {
        return this.summaryCompletedTaskCount(this.projectSummary(project));
    }

    projectProgressCaption(project: PosmProject): string {
        const totalTasks = this.projectTaskCount(project);
        const completedTasks = this.projectCompletedTaskCount(project);
        if (totalTasks > 0) {
            return `${completedTasks}/${totalTasks} delivery schedule tasks complete`;
        }
        if (this.summaryLoading) {
            return 'Loading execution progress...';
        }
        return 'Delivery schedules not generated yet';
    }

    private isTerminalProject(project: PosmProject): boolean {
        const status = this.normalizeCode(project.status);
        return ['COMPLETED', 'CLOSED', 'DELIVERED', 'CANCELLED'].includes(status)
            || this.normalizeCode(project.closureStatus) === 'CLOSED'
            || !!project.closedAt;
    }

    private isOverdue(project: PosmProject): boolean {
        if (this.isTerminalProject(project) || !project.deadline) {
            return false;
        }
        const deadline = this.dateValue(project.deadline);
        if (!deadline) {
            return false;
        }
        return deadline < Date.now();
    }

    private summaryCompletion(summary: PosmProjectSummary): number {
        const value = Number(summary.completionPercentage || 0);
        return Number.isFinite(value) ? value : 0;
    }

    private summaryTaskCount(summary: PosmProjectSummary | null): number {
        return Number(summary?.totalDeliveryScheduleTasks ?? summary?.totalPlannedItems ?? 0);
    }

    private summaryCompletedTaskCount(summary: PosmProjectSummary | null): number {
        return Number(summary?.completedDeliveryScheduleTasks ?? summary?.totalInstalled ?? 0);
    }

    private toSortedOptions(options: Map<string, string>): SelectOption[] {
        return [
            { label: 'All', value: null },
            ...Array.from(options.entries())
                .filter(([value, label]) => !!value && !!label)
                .sort((left, right) => left[1].localeCompare(right[1]))
                .map(([value, label]) => ({ label, value }))
        ];
    }

    private normalizeText(value: unknown): string {
        return `${value || ''}`.trim().toLowerCase();
    }

    private normalizeCode(value: unknown): string {
        return `${value || ''}`
            .trim()
            .toUpperCase()
            .replace(/[\s-]+/g, '_');
    }

    private formatLabel(value: unknown): string {
        const text = `${value || ''}`.trim();
        if (!text) {
            return '';
        }
        return text
            .replace(/_/g, ' ')
            .toLowerCase()
            .replace(/\b\w/g, char => char.toUpperCase());
    }

    private normalizeJobTypeLabel(value: unknown): string {
        const normalized = this.normalizeText(value);
        switch (normalized) {
            case 'installation':
                return 'Installation';
            case 'delivery':
                return 'Delivery';
            case 'removal':
                return 'Removal';
            case 'collection':
                return 'Collection';
            case 'refurbishment':
                return 'Refurbishment';
            case 'rebranding':
                return 'Rebranding';
            case 'site survey':
            case 'site_survey':
                return 'Site Survey';
            default:
                return `${value || ''}`.trim();
        }
    }
}
